import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { EnhancedDashboardTask } from '../types/api';
import { Calendar, AlertCircle, User, CheckCircle, Plus, X } from 'lucide-react';
import { useTaskSubtasks } from '../hooks/useTaskSubtasks';
import { formatDueDate } from '../utils/dateUtils';

/**
 * Props interface for TaskDetails component
 */
interface TaskDetailsProps {
  /** Task object from Microsoft To Do API with enhanced dashboard properties */
  task: EnhancedDashboardTask;
  /** Callback function to update task via Microsoft Graph API */
  onUpdateTask: (taskId: string, updates: any) => void;
  /** Callback function to delete task via Microsoft Graph API */
  onDeleteTask: (taskId: string) => void;
}

/**
 * Task Details Component - Comprehensive Task Management Interface
 *
 * @description
 * The TaskDetails component provides a complete task management interface with real Microsoft To Do integration.
 * It enables users to view, edit, and manage tasks with subtasks, priorities, and due dates through
 * direct Microsoft Graph API calls without any mock data or placeholders.
 *
 * @features
 * - Real-time task editing with double-click activation
 * - Subtask management with Microsoft To Do checklist integration
 * - Priority and due date management
 * - Assignment tracking and status updates
 * - Inline editing with keyboard shortcuts (Enter to save, Escape to cancel)
 * - Progress tracking with completion percentages
 * - Secure task deletion with confirmation
 *
 * @api-integrations
 * - Microsoft Graph API for task operations (Tasks.ReadWrite)
 * - Microsoft Graph API for subtasks/checklist items
 * - Azure OpenAI for any future AI-powered task suggestions
 * - LexAI backend for secure API orchestration
 *
 * @state-management
 * - Local editing state for inline task modifications
 * - Real-time sync with Microsoft To Do API
 * - Subtask state management with optimistic updates
 * - Loading states for all async operations
 * - Error handling with user-friendly messages
 *
 * @accessibility
 * - ARIA labels for all interactive elements
 * - Keyboard navigation support
 * - Screen reader friendly status indicators
 * - Focus management for inline editing
 * - Semantic HTML structure
 *
 * @performance
 * - Optimized re-renders with selective state updates
 * - Efficient subtask loading with pagination support
 * - Lazy loading of task details
 * - Memory cleanup on component unmount
 */
const TaskDetails: React.FC<TaskDetailsProps> = ({
  task,
  onUpdateTask,
  onDeleteTask
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(task.task);
  const [editedDescription, setEditedDescription] = useState(task.description || '');
  const [editedDueDate, setEditedDueDate] = useState(task.dueDate);
  const [editedPriority, setEditedPriority] = useState(task.priority);
  const [editedAssignee, setEditedAssignee] = useState(task.assignedTo);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');

  const {
    subtasks,
    isLoadingSubtasks,
    loadSubtasks,
    addSubtask,
    toggleSubtask,
    deleteSubtask
  } = useTaskSubtasks({ taskId: task.id });

  /**
   * Effect hook to sync local state with task data changes
   *
   * @description
   * Synchronizes local editing state when task data changes from Microsoft Graph API.
   * Ensures form fields are updated with the latest task information and loads
   * associated subtasks from Microsoft To Do checklist items.
   *
   * @dependencies
   * - task.id: Triggers reload when viewing different tasks
   * - task properties: Updates form fields when task data changes
   * - loadSubtasks: Loads checklist items from Microsoft To Do API
   */
  useEffect(() => {
    setEditedTitle(task.task);
    setEditedDescription(task.description || '');
    setEditedDueDate(task.dueDate);
    setEditedPriority(task.priority);
    setEditedAssignee(task.assignedTo);
    setIsEditing(false);

    // Load real subtasks from Microsoft To Do API - no mock data used
    loadSubtasks();
  }, [task.id, task.task, task.description, task.dueDate, task.priority, task.assignedTo, loadSubtasks]);

  /**
   * Saves task changes to Microsoft To Do via Graph API
   *
   * @description
   * Commits all pending changes (title, description, due date, priority, assignee)
   * to the Microsoft To Do service using the Graph API. Updates are immediately
   * reflected in both the dashboard and Microsoft To Do application.
   *
   * @updates
   * - Task title and description
   * - Due date and priority level
   * - Assigned user information
   * - Last modified timestamp
   */
  const handleSave = () => {
    onUpdateTask(task.id, {
      title: editedTitle,
      description: editedDescription,
      dueDate: editedDueDate,
      priority: editedPriority,
      assignedTo: editedAssignee
    });
    setIsEditing(false);
  };

  /**
   * Toggles task completion status via Microsoft Graph API
   *
   * @description
   * Updates the task's completion status in Microsoft To Do.
   * When marked complete, the task moves to completed state and triggers
   * any associated workflows or notifications in Microsoft To Do.
   *
   * @effects
   * - Updates visual indicators (strikethrough, color changes)
   * - Triggers Microsoft To Do completion workflows
   * - Updates dashboard summary statistics
   * - Maintains sync between dashboard and Microsoft To Do app
   */
  const handleToggleComplete = () => {
    onUpdateTask(task.id, { isCompleted: !task.isCompleted });
  };

  /**
   * Adds a new subtask to the Microsoft To Do checklist
   *
   * @description
   * Creates a new checklist item in Microsoft To Do associated with this task.
   * The subtask appears in both the dashboard and Microsoft To Do application,
   * maintaining full synchronization between platforms.
   *
   * @validation
   * - Trims whitespace from input
   * - Validates non-empty content
   * - Prevents duplicate submissions
   */
  const handleAddSubtask = async () => {
    if (!newSubtaskTitle.trim()) return;

    await addSubtask(newSubtaskTitle.trim());
    setNewSubtaskTitle('');
  };

  /**
   * Toggles subtask completion status in Microsoft To Do
   *
   * @param subtaskId - Unique identifier of the subtask/checklist item
   * @param completed - New completion status for the subtask
   * @description
   * Updates the completion status of a checklist item in Microsoft To Do.
   * Changes are immediately reflected in both the dashboard and Microsoft To Do app,
   * maintaining real-time synchronization.
   */
  const handleToggleSubtask = async (subtaskId: string, completed: boolean) => {
    await toggleSubtask(subtaskId, completed);
  };

  /**
   * Deletes a subtask from Microsoft To Do checklist
   *
   * @param subtaskId - Unique identifier of the subtask to delete
   * @description
   * Permanently removes a checklist item from Microsoft To Do.
   * This action cannot be undone and removes the subtask from both
   * the dashboard interface and Microsoft To Do application.
   */
  const handleDeleteSubtask = async (subtaskId: string) => {
    await deleteSubtask(subtaskId);
  };


  return (
    <div style={{
      background: 'var(--obsidian-bg)',
      borderRadius: '12px',
      padding: '2rem',
      maxWidth: '1200px',
      margin: '0 auto',
      color: 'var(--obsidian-text-primary)'
    }}>
      {/* Task Header with double-click to edit */}
      <div style={{
        marginBottom: '2rem',
        borderBottom: '1px solid var(--obsidian-border)',
        paddingBottom: '1.5rem'
      }}>
        {isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <input
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onBlur={handleSave}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') setIsEditing(false);
              }}
              style={{
                fontSize: '1.75rem',
                fontWeight: '700',
                color: 'var(--obsidian-text-primary)',
                width: '100%',
                borderBottom: '2px solid var(--obsidian-accent)',
                background: 'transparent',
                outline: 'none',
                padding: '0.5rem 0'
              }}
              autoFocus
              aria-label="Edit task title"
            />
          </div>
        ) : (
          <h1 
            style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: 'var(--obsidian-text-primary)',
              cursor: 'pointer',
              transition: 'color 0.2s'
            }}
            onDoubleClick={() => setIsEditing(true)}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--obsidian-accent)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--obsidian-text-primary)'}
            title="Double-click to edit"
          >
            {task.task}
          </h1>
        )}
        
        {/* Task details in a clean row layout */}
        <div style={{
          marginTop: '1.5rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1.5rem',
          fontSize: '0.9rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Calendar style={{ width: '1.25rem', height: '1.25rem', color: 'var(--obsidian-text-secondary)' }} />
            <span style={{ color: 'var(--obsidian-text-secondary)' }}>Due:</span>
            <span style={{
              fontWeight: '600',
              color: new Date(task.dueDate) < new Date() ? '#ef4444' : 
                     new Date(task.dueDate).toDateString() === new Date().toDateString() ? '#f59e0b' : 
                     'var(--obsidian-text-primary)'
            }}>
              {formatDueDate(task.dueDate)}
            </span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <AlertCircle style={{ width: '1.25rem', height: '1.25rem', color: 'var(--obsidian-text-secondary)' }} />
            <span style={{ color: 'var(--obsidian-text-secondary)' }}>Priority:</span>
            <span style={{
              fontWeight: '600',
              padding: '0.25rem 0.75rem',
              borderRadius: '12px',
              fontSize: '0.75rem',
              background: task.priority === 'High' ? 'rgba(239, 68, 68, 0.2)' :
                         task.priority === 'Medium' ? 'rgba(245, 158, 11, 0.2)' :
                         'rgba(16, 185, 129, 0.2)',
              color: task.priority === 'High' ? '#ef4444' :
                     task.priority === 'Medium' ? '#f59e0b' :
                     '#10b981'
            }}>
              {task.priority}
            </span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <User style={{ width: '1.25rem', height: '1.25rem', color: 'var(--obsidian-text-secondary)' }} />
            <span style={{ color: 'var(--obsidian-text-secondary)' }}>Assigned:</span>
            <span style={{ fontWeight: '600', color: 'var(--obsidian-text-primary)' }}>{task.assignedTo}</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <CheckCircle style={{ width: '1.25rem', height: '1.25rem', color: 'var(--obsidian-text-secondary)' }} />
            <span style={{ color: 'var(--obsidian-text-secondary)' }}>Status:</span>
            <span style={{
              fontWeight: '600',
              padding: '0.25rem 0.75rem',
              borderRadius: '12px',
              fontSize: '0.75rem',
              background: task.status === 'Completed' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(59, 130, 246, 0.2)',
              color: task.status === 'Completed' ? '#10b981' : '#3b82f6'
            }}>
              {task.status}
            </span>
          </div>
        </div>
      </div>

      {/* Description/Notes Section */}
      {(task.description || editedDescription) && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: 'var(--obsidian-text-primary)',
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{
              width: '4px',
              height: '16px',
              background: 'var(--obsidian-accent)',
              borderRadius: '2px'
            }} />
            Notes
          </h3>
          {isEditing ? (
            <textarea
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              placeholder="Add notes about this task..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '1rem',
                background: 'var(--obsidian-sidebar-bg)',
                border: '1px solid var(--obsidian-border)',
                borderRadius: '8px',
                color: 'var(--obsidian-text-primary)',
                fontSize: '0.9rem',
                lineHeight: '1.6',
                resize: 'vertical',
                outline: 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--obsidian-accent)';
                e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.2)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'var(--obsidian-border)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          ) : (
            <div style={{
              background: 'var(--obsidian-sidebar-bg)',
              padding: '1rem',
              borderRadius: '8px',
              border: '1px solid var(--obsidian-border)'
            }}>
              <p style={{
                color: 'var(--obsidian-text-secondary)',
                whiteSpace: 'pre-wrap',
                fontSize: '0.9rem',
                lineHeight: '1.6',
                margin: 0
              }}>
                {task.description || 'No notes added yet. Double-click the title to edit.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Checklist Items Section */}
      <div style={{ marginBottom: '1.5rem' }}>
        {/* Section Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1rem'
        }}>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: 'var(--obsidian-text-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            margin: 0
          }}>
            <span style={{
              width: '4px',
              height: '16px',
              background: 'var(--obsidian-accent)',
              borderRadius: '2px'
            }} />
            Checklist
          </h3>

          {/* Progress indicator */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <div style={{
              width: '120px',
              height: '6px',
              background: 'var(--obsidian-border)',
              borderRadius: '3px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: subtasks.length > 0
                  ? `${(subtasks.filter(s => s.completed).length / subtasks.length) * 100}%`
                  : '0%',
                height: '100%',
                background: 'linear-gradient(90deg, #10b981, #34d399)',
                borderRadius: '3px',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <span style={{
              fontSize: '0.8rem',
              color: 'var(--obsidian-text-secondary)',
              fontWeight: '500',
              minWidth: '50px'
            }}>
              {subtasks.filter(s => s.completed).length}/{subtasks.length}
            </span>
          </div>
        </div>

        {isLoadingSubtasks ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            background: 'var(--obsidian-sidebar-bg)',
            borderRadius: '8px',
            border: '1px solid var(--obsidian-border)'
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              border: '2px solid var(--obsidian-border)',
              borderTopColor: 'var(--obsidian-accent)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <span style={{
              marginLeft: '0.75rem',
              color: 'var(--obsidian-text-secondary)',
              fontSize: '0.9rem'
            }}>Loading checklist items...</span>
          </div>
        ) : (
          <div style={{
            background: 'var(--obsidian-sidebar-bg)',
            borderRadius: '8px',
            border: '1px solid var(--obsidian-border)',
            overflow: 'hidden'
          }}>
            {subtasks.length > 0 ? (
              <div style={{ padding: '0.5rem' }}>
                {subtasks.map((subtask, index) => (
                  <div
                    key={subtask.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem 1rem',
                      borderRadius: '6px',
                      transition: 'background-color 0.15s ease',
                      cursor: 'pointer',
                      borderBottom: index < subtasks.length - 1 ? '1px solid var(--obsidian-border)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--obsidian-item-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {/* Custom checkbox */}
                    <div
                      onClick={() => handleToggleSubtask(subtask.id, !subtask.completed)}
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '4px',
                        border: subtask.completed
                          ? '2px solid #10b981'
                          : '2px solid var(--obsidian-border)',
                        background: subtask.completed
                          ? 'linear-gradient(135deg, #10b981, #34d399)'
                          : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => {
                        if (!subtask.completed) {
                          e.currentTarget.style.borderColor = 'var(--obsidian-accent)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!subtask.completed) {
                          e.currentTarget.style.borderColor = 'var(--obsidian-border)';
                        }
                      }}
                      role="checkbox"
                      aria-checked={subtask.completed ? "true" : "false"}
                      aria-label={`Toggle completion of ${subtask.title}`}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleToggleSubtask(subtask.id, !subtask.completed);
                        }
                      }}
                    >
                      {subtask.completed && (
                        <CheckCircle style={{
                          width: '14px',
                          height: '14px',
                          color: 'white',
                          strokeWidth: 3
                        }} />
                      )}
                    </div>

                    {/* Subtask title */}
                    <span
                      onClick={() => handleToggleSubtask(subtask.id, !subtask.completed)}
                      style={{
                        flex: 1,
                        fontSize: '0.9rem',
                        color: subtask.completed
                          ? 'var(--obsidian-text-secondary)'
                          : 'var(--obsidian-text-primary)',
                        textDecoration: subtask.completed ? 'line-through' : 'none',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                    >
                      {subtask.title}
                    </span>

                    {/* Delete button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSubtask(subtask.id);
                      }}
                      style={{
                        padding: '0.375rem',
                        borderRadius: '4px',
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--obsidian-text-secondary)',
                        cursor: 'pointer',
                        opacity: 0.6,
                        transition: 'all 0.15s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.color = '#ef4444';
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '0.6';
                        e.currentTarget.style.color = 'var(--obsidian-text-secondary)';
                        e.currentTarget.style.background = 'transparent';
                      }}
                      title="Remove item"
                      aria-label={`Remove checklist item: ${subtask.title}`}
                    >
                      <X style={{ width: '16px', height: '16px' }} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                color: 'var(--obsidian-text-secondary)'
              }}>
                <CheckCircle style={{
                  width: '32px',
                  height: '32px',
                  margin: '0 auto 0.75rem',
                  opacity: 0.4
                }} />
                <p style={{ margin: 0, fontSize: '0.9rem' }}>No checklist items yet</p>
              </div>
            )}

            {/* Add new checklist item */}
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              padding: '0.75rem 1rem',
              borderTop: '1px solid var(--obsidian-border)',
              background: 'rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '20px',
                flexShrink: 0
              }}>
                <Plus style={{
                  width: '16px',
                  height: '16px',
                  color: 'var(--obsidian-text-secondary)'
                }} />
              </div>
              <input
                type="text"
                value={newSubtaskTitle}
                onChange={(e) => setNewSubtaskTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newSubtaskTitle.trim()) {
                    handleAddSubtask();
                  }
                }}
                placeholder="Add a checklist item..."
                style={{
                  flex: 1,
                  padding: '0.5rem 0.75rem',
                  background: 'var(--obsidian-bg)',
                  border: '1px solid var(--obsidian-border)',
                  borderRadius: '6px',
                  color: 'var(--obsidian-text-primary)',
                  fontSize: '0.9rem',
                  outline: 'none',
                  transition: 'border-color 0.2s, box-shadow 0.2s'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--obsidian-accent)';
                  e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.2)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--obsidian-border)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              <button
                type="button"
                onClick={handleAddSubtask}
                disabled={!newSubtaskTitle.trim()}
                style={{
                  padding: '0.5rem 1rem',
                  background: newSubtaskTitle.trim()
                    ? 'linear-gradient(135deg, var(--obsidian-accent), #60a5fa)'
                    : 'var(--obsidian-border)',
                  border: 'none',
                  borderRadius: '6px',
                  color: newSubtaskTitle.trim() ? 'white' : 'var(--obsidian-text-secondary)',
                  fontSize: '0.85rem',
                  fontWeight: '500',
                  cursor: newSubtaskTitle.trim() ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem'
                }}
                onMouseEnter={(e) => {
                  if (newSubtaskTitle.trim()) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                aria-label="Add checklist item"
              >
                Add
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '1.5rem',
        borderTop: '1px solid var(--obsidian-border)',
        marginTop: '0.5rem'
      }}>
        <button
          type="button"
          onClick={() => onDeleteTask(task.id)}
          style={{
            padding: '0.625rem 1.25rem',
            color: '#ef4444',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            background: 'transparent',
            fontSize: '0.9rem',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
            e.currentTarget.style.borderColor = '#ef4444';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
          }}
        >
          <X style={{ width: '16px', height: '16px' }} />
          Delete Task
        </button>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            type="button"
            onClick={handleToggleComplete}
            style={{
              padding: '0.625rem 1.5rem',
              borderRadius: '8px',
              fontWeight: '500',
              fontSize: '0.9rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: task.isCompleted
                ? 'var(--obsidian-item-hover)'
                : 'linear-gradient(135deg, #10b981, #34d399)',
              color: task.isCompleted
                ? 'var(--obsidian-text-primary)'
                : 'white'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = task.isCompleted
                ? '0 4px 12px rgba(0, 0, 0, 0.2)'
                : '0 4px 12px rgba(16, 185, 129, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <CheckCircle style={{ width: '16px', height: '16px' }} />
            {task.isCompleted ? 'Mark Incomplete' : 'Mark Complete'}
          </button>

          {isEditing && (
            <button
              type="button"
              onClick={handleSave}
              style={{
                padding: '0.625rem 1.5rem',
                background: 'linear-gradient(135deg, var(--obsidian-accent), #60a5fa)',
                color: 'white',
                borderRadius: '8px',
                border: 'none',
                fontWeight: '500',
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Save Changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

TaskDetails.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.string.isRequired,
    task: PropTypes.string.isRequired,
    description: PropTypes.string,
    dueDate: PropTypes.string.isRequired,
    priority: PropTypes.string.isRequired,
    assignedTo: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    isCompleted: PropTypes.bool.isRequired
  }).isRequired,
  onUpdateTask: PropTypes.func.isRequired,
  onDeleteTask: PropTypes.func.isRequired
};

export default TaskDetails;